import Clear from '@mui/icons-material/Clear';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Search from '@mui/icons-material/Search';
import { debounce } from '@mui/material/utils';
import type {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridRowsProp,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { DataGridPro as DataGrid } from '@mui/x-data-grid-pro';
import { escapeRegExp } from '@mui/x-data-grid-pro/internals';
import type { Filter } from '@src/common/components/Filters';
import { Filters } from '@src/common/components/Filters';
import { IconButton } from '@src/common/components/IconButton';
import {
  tableContainer,
  tableStyle,
} from '@src/common/components/tableStyle/TableStyle';
import { TextInput } from '@src/common/components/TextInput';
import { Typography } from '@src/common/components/Typography';
import { dataGridFrenchText } from '@src/common/i18n/DataGridLocaleText.fr';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SortBy } from 'src/orders/hooks/useOrdersWithFilters';
import { Stack } from './Stack';

type Props<T extends FieldValues> = {
  columns: GridColDef[];
  rows: Record<string, string | unknown>[];
  ariaLabel?: string;
  rightHeaderBlock?: React.ReactNode;
  onRowClick?: (params: GridRowParams) => void;
  filters?: {
    onChange: (value: T) => void;
    filters: Filter<T>[];
  };
  pinnedRows?: {
    rows: GridRowsProp;
    collapsibleLabel: string;
  };
};

type ClientModeProps<T extends FieldValues> = {
  paginationAndSortMode: 'client';
  pageSize?: number;
  currentPage?: number;
  onCurrentPageChange?: (props: GridPaginationModel) => void;
} & Props<T>;

type ServerModeProps<T extends FieldValues> = {
  onCurrentPageChange: (props: GridPaginationModel) => void;
  totalRowCount: number;
  isLoading: boolean;
  paginationAndSortMode: 'server';
  pageSize: number;
  currentPage: number;
  onSearchValue: (search: string) => void;
  onSort: (props?: SortBy) => void;
} & Props<T>;

const defaultProps = {
  onCurrentPageChange: undefined,
  totalRowCount: undefined,
  isLoading: undefined,
  currentPage: undefined,
  onSearchValue: undefined,
  onSort: undefined,
  pinnedRows: undefined,
};

export const FilterableTable = <T extends FieldValues>(
  paginationModeProps: ClientModeProps<T> | ServerModeProps<T>,
) => {
  const props = { ...defaultProps, ...paginationModeProps };
  const isServerMode = props.paginationAndSortMode === 'server';
  const { serverRows } = useServerRows({ rows: props.rows });
  const { clientRows, clientRequestSearch } = useClientRows({
    rows: props.rows,
  });
  const rows = isServerMode ? serverRows : clientRows;

  const {
    i18n: { language },
  } = useTranslation();
  const [hidePinnedRows, setHidePinnedRows] = useState(false);

  const paginationModel =
    props.currentPage != undefined
      ? {
          page: props.currentPage,
          pageSize: props.pageSize ?? 100,
        }
      : undefined;

  const pinnedRows = props.pinnedRows
    ? {
        top: [
          {
            id: 'buttonRow',
            hidePinnedRowsButton: true,
          },
          ...props.pinnedRows.rows,
        ],
      }
    : undefined;

  const columnsWithHidePinnedRowsButton = () => {
    return props.columns.map((column, index) => ({
      ...column,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.hidePinnedRowsButton) {
          return index === 0 ? (
            <Stack direction="row" gap={1}>
              <IconButton
                style={{ padding: 0 }}
                onClick={togglePinnedRowsVisibility}
              >
                {hidePinnedRows ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>

              <Typography component="label">
                {props.pinnedRows?.collapsibleLabel}
              </Typography>
            </Stack>
          ) : null;
        }
        return column.renderCell ? column.renderCell(params) : params.value;
      },
    }));
  };

  const sortTable = (model: GridSortModel) => {
    if (model.length === 0) {
      props.onSort?.(undefined);
      return;
    }
    const { field, sort } = model[0];
    if (!sort) {
      props.onSort?.(undefined);
      return;
    }
    props.onSort?.({ field, direction: sort });
  };

  const togglePinnedRowsVisibility = () => {
    setHidePinnedRows((prev) => !prev);
  };

  // https://v6.mui.com/x/react-data-grid/pagination/#server-side-pagination
  // Following lines are here to prevent `rowCountState` from being 0/undefined during the loading
  const [rowCount, setRowCount] = useState(props.totalRowCount);
  useEffect(() => {
    setRowCount((prevRowCountState) =>
      props.isLoading ? prevRowCountState : props.totalRowCount,
    );
  }, [props.isLoading, props.totalRowCount, setRowCount]);
  return (
    <Stack
      gap={theme.spacing['5']}
      className={tableContainer}
      aria-label={props.ariaLabel}
      minHeight="235px"
      flex="1"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        paddingInline={theme.spacing['4']}
      >
        <Stack direction="row" gap={theme.spacing['2']}>
          <TextSearch onChange={props.onSearchValue ?? clientRequestSearch} />
          {props.filters && (
            <Filters
              filters={props.filters.filters}
              onChange={props.filters.onChange}
            />
          )}
        </Stack>

        {props.rightHeaderBlock}
      </Stack>
      <DataGrid
        pagination
        className={`${tableStyle} ${hidePinnedRows ? pinnedRowsHiddenStyle : ''}`}
        columns={columnsWithHidePinnedRowsButton()}
        showColumnVerticalBorder={false}
        rows={rows}
        onRowClick={props.onRowClick}
        getRowClassName={() => (props.onRowClick ? ClickableRow : Row)}
        initialState={{ pagination: { paginationModel } }}
        disableColumnFilter={isServerMode}
        disableColumnMenu={isServerMode}
        onSortModelChange={sortTable}
        rowCount={rowCount}
        loading={props.isLoading}
        sortingMode={props.paginationAndSortMode ?? 'client'}
        paginationMode={props.paginationAndSortMode ?? 'client'}
        paginationModel={paginationModel}
        rowSelection={false}
        localeText={language === 'fr' ? dataGridFrenchText : undefined}
        onPaginationModelChange={props.onCurrentPageChange}
        pinnedRows={pinnedRows}
      />
    </Stack>
  );
};

const pinnedRowsHiddenStyle = css({
  '& .MuiDataGrid-pinnedRows--top > :not(:first-of-type)': {
    display: 'none', // Hide other children when pinned rows are hidden
  },
});

type TextSearchProps = {
  onChange: (searchValue: string) => void;
};

const TextSearch = (props: TextSearchProps) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');

  const onChange = (text: string) => {
    setSearchText(text);
    props.onChange(text);
  };

  const showClearButton = searchText !== '';

  return (
    <TextInput
      variant="outlined"
      className={TextSearchStyle}
      value={searchText}
      onChange={(e) => onChange(e.target.value)}
      placeholder={t('searchPlaceholder')}
      slotProps={{
        input: {
          startAdornment: <Search />,
          endAdornment: showClearButton ? (
            <IconButton onClick={() => onChange('')}>
              <Clear />
            </IconButton>
          ) : undefined,
        },
      }}
    />
  );
};

const useServerRows = ({
  rows,
}: {
  rows: Record<string, string | unknown>[];
}) => {
  const [serverRows, setServerRows] = useState(rows);
  useEffect(() => {
    if (JSON.stringify(serverRows) === JSON.stringify(rows)) {
      return;
    }
    setServerRows(rows);
  }, [rows, serverRows]);

  return { serverRows };
};

const useClientRows = ({
  rows,
}: {
  rows: Record<string, string | unknown>[];
}) => {
  const [clientSearchRequest, setClientSearchRequest] = useState('');
  const searchRegex = new RegExp(escapeRegExp(clientSearchRequest), 'i');
  const clientRows = rows.filter((row) => {
    return Object.keys(row).some((field) => {
      if (typeof row[field] !== 'string') {
        return false;
      }
      return searchRegex.test(row[field]);
    });
  });

  const clientRequestSearch = debounce((searchValue: string) => {
    setClientSearchRequest(searchValue);
  }, 100);

  return { clientRows, clientRequestSearch };
};

const TextSearchStyle = css({
  borderRadius: '28px',
  backgroundColor: theme.palette.surface.surfaceContainer,
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-input': {
    padding: `${theme.spacing['2']} ${theme.spacing['3']}`,
  },
});

const ClickableRow = css({
  cursor: 'pointer',
});

const Row = css({
  cursor: 'default',
});
